/* @flow */
import Espree from 'espree'
import BlocklifyHandlerRegistration from './BlocklifyHandlers'
import BlocklifyApiInterceptorRegistration from './BlocklifyApiInterceptors'
import { getXmlEscaper } from './BlocklifyUtil'

const escapeXml = getXmlEscaper()

// Functions returning xml when given an espree js syntax node (and a walker for the continued descents)
const astNodeHandlers = {}
function registerHandler(nodeType: string, handlerFn: any) {
  astNodeHandlers[nodeType] = handlerFn
}
BlocklifyHandlerRegistration(registerHandler)
// Functions returning xml which only handle a given node type under some specific circumstances (detection and handling of API calls)
const astApiInterceptors = {}
// NOTE: The order of registration is significant since the first interceptor for a given node type to output xml wins and they're run in order of registration
function registerApiInterceptor(nodeType: string, interceptorFn: any) {
  if (!astApiInterceptors[nodeType]) {
    astApiInterceptors[nodeType] = []
  }
  astApiInterceptors[nodeType].push(interceptorFn)
}
BlocklifyApiInterceptorRegistration(registerApiInterceptor)

function getCodeForNode(code, node) {
  if (!node || !code) return ''
  return code.substr(node.start, node.end - node.start)
}

function injectCommentIfAny(node, xml) {
  // We drop trailing comments as they don't map well to block comments
  if (xml && node && node.leadingComments && node.leadingComments.length > 0) {
    const joined = node.leadingComments.map(c => c.value && c.value.trim()).join('\n')
    const commentElement = `<comment>${joined}</comment>`
    // Replace opening of first element with opening + comment
    return xml.replace(/^(<[^>]+>)/, `$1${commentElement}`)
  }
  return xml
}

// Fallback
function statementFallbackHandler(code, node) {
  let statement = getCodeForNode(code, node)
  return `<block type="javascript_arbitrary_statement"><field name="statement">${escapeXml(
    statement
  )}</field></block>`
}

function expressionFallbackHandler(code, node) {
  let expression = getCodeForNode(code, node)
  return `<block type="javascript_arbitrary_expression"><field name="expression">${escapeXml(
    expression
  )}</field></block>`
}

function recursor(code, node, isStatement) {
  const nodeCode = getCodeForNode(code, node)
  if (!node) return ''
  if (window.blocklyDebug) {
    console.debug('recursing into', node.type, nodeCode)
  }
  let xml = null
  const interceptors = astApiInterceptors[node.type]
  if (interceptors) {
    for (const interceptor of interceptors) {
      try {
        xml = interceptor(code, node, walker, isStatement)
      } catch (e) {
        console.warn('Error in interceptor for node', node.type, nodeCode, e)
      }
      if (xml != null) break
    }
  }
  if (!xml) {
    const handler = astNodeHandlers[node.type]
    if (handler) {
      try {
        xml = handler(code, node, walker, isStatement)
      } catch (e) {
        console.warn('Error handling node', e, node.type, nodeCode)
      }
    }
  }
  const fallbackHandler = isStatement ? statementFallbackHandler : expressionFallbackHandler
  xml = xml != null ? xml : fallbackHandler(code, node)
  xml = injectCommentIfAny(node, xml)
  return xml
}

// The oject given to the handlers so they can continue recursive descent specifying either expression or statement
const walker = {
  stmt(code, node) {
    return recursor(code, node, true)
  },
  expr(code, node) {
    return recursor(code, node, false)
  }
}

function jsToBlocks(code: string) {
  const espreeConfig = {
    loc: true,
    attachComment: true,
    ecmaFeatures: { globalReturn: true },
    ecmaVersion: 7
  }
  const ast = Espree.parse(code, espreeConfig)
  const generatedXml = `<xml xmlns="http://www.w3.org/1999/xhtml"><variables></variables>${walker.stmt(
    code,
    ast
  )}</xml>`
  if (window.blocklyDebug) {
    console.debug('Generated xml ' + generatedXml)
  }
  return generatedXml
}

export default {
  jsToBlocks,
  registerHandler,
  registerApiInterceptor
}
